import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { File, Paths } from 'expo-file-system';
import * as Zeego from 'zeego/context-menu';
import Clipboard from '@react-native-clipboard/clipboard';
import mime from 'mime';

//iOS Uniform Type Identifiers for common image formats (used by share sheet preview).
const UTI_BY_EXT = {
    jpg: 'public.jpeg',
    jpeg: 'public.jpeg',
    png: 'public.png',
    gif: 'com.compuserve.gif',
    webp: 'org.webmproject.webp',
    heic: 'public.heic',
};

const getExtFromUri = (uri) => {
    const clean = uri.split('?')[0].split('#')[0];
    const ext = clean.split('.').pop()?.toLowerCase();
    return ext && ext.length <= 5 ? ext : 'jpg';
};

const getFilenameFromUri = (uri) => {
    const clean = uri.split('?')[0].split('#')[0];
    const name = clean.split('/').pop();
    return name && name.includes('.') ? name : `image.${getExtFromUri(uri)}`;
};

const ImageContextWrapper = ({ children, imageUri, onCopy, onSave }) => {
    const handleCopy = useCallback(async () => {
        if (onCopy) return onCopy(imageUri);
        try {
            let base64;
            if (imageUri.startsWith('data:')) {
                base64 = imageUri.split(',')[1];
            } else {
                const file = new File(imageUri);
                base64 = await file.base64();
            }
            if (base64) Clipboard.setImage(base64);
        } catch {
            // clipboard write failed silently
        }
    }, [imageUri, onCopy]);

    const handleSave = useCallback(async () => {
        if (onSave) return onSave(imageUri);
        try {
            const { shareAsync } = await import('expo-sharing');
            const ext = getExtFromUri(imageUri);
            const filename = getFilenameFromUri(imageUri);
            const dest = new File(Paths.cache, filename);
            if (dest.exists) dest.delete();
            const source = new File(imageUri);
            await source.copy(dest);

            const options = {};
            if (Platform.OS === 'ios') {
                options.UTI = UTI_BY_EXT[ext] || 'public.jpeg';
            } else if (Platform.OS === 'android') {
                options.mimeType = mime.getType(ext) || 'image/jpeg';
            }
            await shareAsync(dest.uri, options);
        } catch {
            // save failed silently
        }
    }, [imageUri, onSave]);

    // Web: skip the context menu — the browser's native right-click is sufficient.
    if (Platform.OS === 'web') {
        return children;
    }

    return (
        <Zeego.Root>
            <Zeego.Trigger>
                {children}
            </Zeego.Trigger>
            <Zeego.Content>
                <Zeego.Item key="copy" onSelect={handleCopy}>
                    <Zeego.ItemIcon
                        ios={{ name: 'doc.on.doc', pointSize: 16 }}
                        androidIconName="content_copy"
                    />
                    <Zeego.ItemTitle>Copy Image</Zeego.ItemTitle>
                </Zeego.Item>
                <Zeego.Item key="save" onSelect={handleSave}>
                    <Zeego.ItemIcon
                        ios={{ name: 'square.and.arrow.up', pointSize: 16 }}
                        androidIconName="share"
                    />
                    <Zeego.ItemTitle>Share Image</Zeego.ItemTitle>
                </Zeego.Item>
            </Zeego.Content>
        </Zeego.Root>
    );
};

export default ImageContextWrapper;
